// apiUsageTracker.js
/**
 * Utility for tracking API usage and rate limits
 * Simplified to focus on actual limit proximity with no projections
 */

// Constants for API usage tracking
const RATE_LIMIT_STORAGE_KEY = 'anthropic_rate_limit_state';
const USAGE_RESET_TIME = 3600000; // 1 hour in milliseconds

// Warning thresholds (percentages)
const WARNING_THRESHOLDS = [80, 90, 95];

/**
 * Track API call and update usage statistics
 * 
 * @param {Object} response - The response from the API call
 * @returns {void}
 */
export const trackApiCall = (response) => {
  let usageState = loadUsageState();
  
  // Update call count
  usageState.callCount += 1;
  
  // If we have rate limit information in the response headers
  if (response?.headers && response.headers['x-ratelimit-remaining'] && response.headers['x-ratelimit-limit']) {
    usageState.remaining = parseInt(response.headers['x-ratelimit-remaining'], 10);
    usageState.limit = parseInt(response.headers['x-ratelimit-limit'], 10);
    
    // If reset time header is available
    if (response.headers['x-ratelimit-reset']) {
      usageState.resetTime = parseInt(response.headers['x-ratelimit-reset'], 10) * 1000; // Convert to ms
    } else {
      // Default to 1 hour from now
      usageState.resetTime = Date.now() + USAGE_RESET_TIME;
    }
    
    // Record actual usage percentage
    usageState.usagePercentage = calculateUsagePercentage(usageState);
    
    // Update last alert time record for any newly crossed thresholds
    updateThresholdAlerts(usageState);
  } else {
    // Without headers, just track call count
    usageState.lastCallTime = Date.now();
    
    // If this is the first call in a window, set the reset time
    if (usageState.callCount === 1) {
      usageState.startTime = Date.now();
      usageState.resetTime = Date.now() + USAGE_RESET_TIME;
    }
  }
  
  // Save the updated state
  saveUsageState(usageState);
};

/**
 * Calculate the actual usage percentage based on remaining and limit values
 * 
 * @param {Object} usageState - The current usage state
 * @returns {number} - Percentage of rate limit used (0-100)
 */
const calculateUsagePercentage = (usageState) => {
  if (usageState.limit === null || usageState.remaining === null) {
    return 0;
  }
  
  const used = usageState.limit - usageState.remaining;
  return Math.min(100, Math.round((used / usageState.limit) * 100));
};

/**
 * Update threshold alert records for any newly crossed thresholds
 * 
 * @param {Object} usageState - The current usage state
 */
const updateThresholdAlerts = (usageState) => {
  if (!usageState.alertedThresholds) {
    usageState.alertedThresholds = {};
  }
  
  for (const threshold of WARNING_THRESHOLDS) {
    // If we've crossed this threshold but haven't alerted it yet in this window
    if (usageState.usagePercentage >= threshold && !usageState.alertedThresholds[threshold]) {
      usageState.alertedThresholds[threshold] = Date.now();
    }
  }
};

/**
 * Track API error related to rate limits
 * 
 * @param {Object} error - The error from the API call
 * @returns {void}
 */
export const trackApiError = (error) => {
  // Check if this is a rate limit error
  const isRateLimitError = 
    error?.status === 429 || 
    error?.message?.includes('rate limit') ||
    error?.message?.includes('too many requests');
  
  if (isRateLimitError) {
    let usageState = loadUsageState();
    
    // Mark as rate limited
    usageState.isRateLimited = true;
    usageState.usagePercentage = 100; // Force to 100% when rate limited
    
    // If the error has a retry-after header, use that
    if (error?.headers && error.headers['retry-after']) {
      const retryAfterSeconds = parseInt(error.headers['retry-after'], 10);
      usageState.resetTime = Date.now() + (retryAfterSeconds * 1000);
    } else {
      // Otherwise, estimate reset time (1 hour from now)
      usageState.resetTime = Date.now() + USAGE_RESET_TIME;
    }
    
    // Save the updated state
    saveUsageState(usageState);
  }
};

/**
 * Get the current rate limit usage percentage
 * 
 * @returns {number} - Percentage of rate limit used (0-100)
 */
export const getRateLimitPercentage = () => {
  const usageState = loadUsageState();
  
  // Handle hard rate limit case
  if (usageState.isRateLimited) {
    return 100;
  }
  
  // Return actual usage percentage if available
  if (usageState.usagePercentage !== undefined) {
    return usageState.usagePercentage;
  }
  
  // If we have explicit limit values, calculate percentage
  if (usageState.remaining !== null && usageState.limit !== null) {
    return calculateUsagePercentage(usageState);
  }
  
  // Default if no data
  return 0;
};

/**
 * Get the highest threshold that has been crossed but not yet alerted
 * Returns null if no new thresholds crossed
 * 
 * @returns {number|null} - The threshold percentage or null
 */
export const getActiveWarningThreshold = () => {
  const usageState = loadUsageState();
  const currentPercentage = getRateLimitPercentage();
  
  // No need to check if we're not approaching any thresholds
  if (currentPercentage < WARNING_THRESHOLDS[0]) {
    return null;
  }
  
  // Check for thresholds that have been crossed
  const crossedThresholds = WARNING_THRESHOLDS.filter(threshold => 
    currentPercentage >= threshold
  );
  
  if (crossedThresholds.length === 0) {
    return null;
  }
  
  // Get the highest crossed threshold
  const highestThreshold = Math.max(...crossedThresholds);
  
  // If we already alerted for this threshold within the cooldown period, don't alert again
  const ALERT_COOLDOWN = 5 * 60 * 1000; // 5 minutes in ms
  if (usageState.alertedThresholds && 
      usageState.alertedThresholds[highestThreshold] && 
      (Date.now() - usageState.alertedThresholds[highestThreshold]) < ALERT_COOLDOWN) {
    return null;
  }
  
  return highestThreshold;
};

/**
 * Check if we're approaching the rate limit (any threshold crossed)
 * 
 * @returns {boolean} - True if approaching rate limit
 */
export const isApproachingRateLimit = () => {
  return getActiveWarningThreshold() !== null || isRateLimited();
};

/**
 * Check if we're rate limited
 * 
 * @returns {boolean} - True if rate limited
 */
export const isRateLimited = () => {
  const usageState = loadUsageState();
  
  // If marked as rate limited, check if we've passed the reset time
  if (usageState.isRateLimited) {
    if (Date.now() > usageState.resetTime) {
      // Reset the rate limit flag
      usageState.isRateLimited = false;
      
      // Clear alerted thresholds when reset occurs
      usageState.alertedThresholds = {};
      
      saveUsageState(usageState);
      return false;
    }
    return true;
  }
  
  return false;
};

/**
 * Mark a threshold as alerted to prevent duplicate alerts
 * 
 * @param {number} threshold - The threshold that was alerted
 */
export const markThresholdAlerted = (threshold) => {
  const usageState = loadUsageState();
  
  if (!usageState.alertedThresholds) {
    usageState.alertedThresholds = {};
  }
  
  usageState.alertedThresholds[threshold] = Date.now();
  saveUsageState(usageState);
};

/**
 * Get time remaining until rate limit reset
 * 
 * @returns {number} - Milliseconds until reset, or 0 if not rate limited
 */
export const getTimeUntilReset = () => {
  const usageState = loadUsageState();
  
  if (usageState.resetTime) {
    const timeRemaining = usageState.resetTime - Date.now();
    return Math.max(0, timeRemaining);
  }
  
  return 0;
};

/**
 * Get a user-friendly time representation
 * 
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} - Formatted time string (e.g., "1 hour 3 minutes")
 */
export const getFormattedTimeRemaining = () => {
  const ms = getTimeUntilReset();
  
  if (ms <= 0) return "now";
  
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
  
  if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}${minutes > 0 ? ` ${minutes} minute${minutes !== 1 ? 's' : ''}` : ''}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
};

/**
 * Reset usage tracking
 */
export const resetUsageTracking = () => {
  // Remove from localStorage
  localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
  
  // Then save a fresh state
  saveUsageState(getDefaultUsageState());
};

/**
 * Load usage state from localStorage
 * 
 * @returns {Object} - The usage state
 */
const loadUsageState = () => {
  try {
    const storedState = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
    if (storedState) {
      const parsedState = JSON.parse(storedState);
      
      // Check if the stored state is stale (reset time has passed)
      if (parsedState.resetTime && Date.now() > parsedState.resetTime) {
        return getDefaultUsageState();
      }
      
      return parsedState;
    }
  } catch (error) {
    console.error('Error loading API usage state:', error);
  }
  
  return getDefaultUsageState();
};

/**
 * Save usage state to localStorage
 * 
 * @param {Object} state - The usage state to save
 */
const saveUsageState = (state) => {
  try {
    localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving API usage state:', error);
  }
};

/**
 * Get the default usage state
 * 
 * @returns {Object} - The default usage state
 */
const getDefaultUsageState = () => {
  return {
    callCount: 0,
    startTime: null,
    lastCallTime: null,
    resetTime: null,
    remaining: null,
    limit: null,
    usagePercentage: 0,
    isRateLimited: false,
    alertedThresholds: {}
  };
};

export default {
  trackApiCall,
  trackApiError,
  getRateLimitPercentage,
  isApproachingRateLimit,
  isRateLimited,
  getTimeUntilReset,
  getFormattedTimeRemaining,
  getActiveWarningThreshold,
  markThresholdAlerted,
  resetUsageTracking
};